import {
	ApplicationConfig,
	inject,
	provideAppInitializer,
	provideCheckNoChangesConfig,
	provideZonelessChangeDetection
} from '@angular/core'
import {
	NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouteReuseStrategy, Scroll, provideRouter,
	withInMemoryScrolling
} from '@angular/router'
import { ViewportScroller } from '@angular/common'
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http'
import { provideTransloco } from '@jsverse/transloco'
import { provideLoggerTokens } from '@ibsoftware/ng-logger'
import { FrontEndType, provideSession, sessionInterceptor } from '@ibsoftware/ng-session'

import { routes } from './routes/app.routes'
import { translocoOptions } from './config/transloco'
import { environment } from '../environments/environment'
import { AppInitService } from './app-init.service'
import { providePlayerApiConfig } from './api/player-api/player-api-config'
import {provideSocket} from '@ibsoftware/ng-web-socket'
import {corsInterceptor} from '@/routes/interceptors/cors-http-interceptor'
import { LOGGER_MODULES } from '@/config/logger-modules'
import { CasinoRouteReuseStrategy } from '@/features/casino/casino-route-reuse.strategy'

// Zoneless safety net (docs/zoneless-migration-plan.md). Enabled only by environment.local.ts.
const zonelessDebug = environment.zonelessDebug === true

export const appConfig: ApplicationConfig = {
	providers: [
		provideZonelessChangeDetection(),
		// Every second, re-check all views (OnPush included) and throw NG0100 for any binding that changed without
		// notifying Angular: without zone.js such a binding silently shows stale data.
		...(zonelessDebug ? [provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 })] : []),
		// New pages open at the top, Back restores the position. Query-only
		// navigations opt out with `scroll: 'manual'`.
		provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
		// Keeps a casino lobby alive behind the game opened from it.
		{ provide: RouteReuseStrategy, useExisting: CasinoRouteReuseStrategy },
		// Here, not in the strategy: the router depends on the strategy.
		provideAppInitializer(() => {
			const strategy = inject(CasinoRouteReuseStrategy)
			const router = inject(Router)
			const viewportScroller = inject(ViewportScroller)

			router.events.subscribe((event) => {
				if (event instanceof NavigationStart) {
					strategy.handleNavigationStart()
				} else if (event instanceof Scroll) {
					// Only Back carries a position. A microtask lands after the router's restore.
					if (event.position && strategy.isRebuiltLobby(router.routerState.snapshot.root)) {
						queueMicrotask(() => viewportScroller.scrollToPosition([0, 0], { behavior: 'instant' }))
					}
				} else if (event instanceof NavigationEnd) {
					strategy.handleNavigationDone(event.urlAfterRedirects)
				} else if (event instanceof NavigationCancel || event instanceof NavigationError) {
					strategy.handleNavigationDone(null)
				}
			})
		}),
		provideSocket({ rootUrl: environment.socketRootUrl }),
		provideSession({ frontEndType: FrontEndType.bettingSite, authRootUrl: environment.apiRootUrl }),
		provideHttpClient(
			withXhr(),
			withInterceptors([sessionInterceptor, corsInterceptor])
		),
		provideTransloco(translocoOptions),
		// Every module is handed to the lib so it never gates by module - LoggerService owns that. The env log level still applies.
		provideLoggerTokens([...LOGGER_MODULES], environment.logLevel),
		providePlayerApiConfig(environment.apiRootUrl),
		// Initialize the app with all necessary services
		// This will ensure that the app and required data is ready before the first render
		provideAppInitializer(() => {
			const initService = inject(AppInitService)
			return initService.init()
		})
	]
}

